import { type ComponentProps, useEffect, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type { UptimeLiveProbeState } from "@/v2-backoffice-client/uptime/view/runProbe/useUptimeLiveProbe"

export type UptimeLiveIndicatorProps = ComponentProps<"span"> & {
  state: UptimeLiveProbeState
}

// Renders the small "live / next probe in Xs / stopped" pill that
// lives next to the range switcher on the response-times chart. The
// Xs countdown ticks once per second via a tiny local interval so we
// don't drag the chart into a re-render loop.
function UptimeLiveIndicator(props: UptimeLiveIndicatorProps) {
  const { state, className, ...spanProps } = props

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (state.nextProbeAt == null) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [state.nextProbeAt])

  const label = (() => {
    if (state.isStopped) return "Live paused"
    if (state.isProbing) return "Probing…"
    if (state.nextProbeAt == null) return "Live"
    const seconds = Math.max(0, Math.round((state.nextProbeAt - now) / 1000))
    return `Live · next in ${seconds}s`
  })()

  const variant = state.isStopped
    ? "stopped"
    : state.isProbing
      ? "active"
      : "idle"

  return (
    <span
      className={cn(
        "uptime-live-indicator",
        `uptime-live-indicator-${variant}`,
        className,
      )}
      title={
        state.isStopped
          ? "Live probing stopped after the open-tab cap. Close and reopen the sheet to resume."
          : "Backoffice is probing this target directly while you view it, in addition to the cron job."
      }
      {...spanProps}
    >
      <span className="uptime-live-indicator-dot" aria-hidden />
      {label}
    </span>
  )
}

export default UptimeLiveIndicator
